import { z } from 'zod';
import { isoTimestamp } from '../schemas/common.js';
import { HOUSEHOLD_TIMEZONE } from '../enums.js';
/**
 * Recurring schedules (spec §7).
 *
 * The recurrence **rule** is stored on the row; concrete occurrences are never
 * stored — they are expanded on demand from the rule, here, in code both the
 * client and the server run, so the two always agree about which Tuesdays a
 * weekly daycare falls on. Storing occurrences would double every edit and let
 * the two representations drift.
 *
 * The app's own editor only ever writes **weekly** (every N weeks, on the base
 * row's weekday). The other shapes exist because the household's real calendar
 * is a shared Google Calendar (docs/CALENDAR_SYNC.md) and phones make daily,
 * monthly ("every 2nd Tuesday"), yearly (birthdays) and multi-weekday series;
 * calendar sync translates RRULEs into these so Huib and the week view see the
 * same occurrences the phones do:
 *
 * - `daily` / `weekly` / `monthly` / `yearly`, every `interval` periods;
 * - `weekdays` (0 = Sunday … 6 = Saturday): weekly — which days of the week
 *   (absent = the base row's weekday); monthly with `nth` — which weekday;
 * - `nth` (1–5, or -1 = last): monthly on the nth `weekdays[0]` instead of on the
 *   base row's day of the month;
 * - `custom`: a rule nothing here can model. Kept verbatim in `rrule` so it
 *   round-trips to the calendar untouched; expands to the base occurrence only.
 *
 * Expansion keeps **local wall-clock time** across DST (a weekly 10:00 stays
 * 10:00 in November), the way every calendar app does — a fixed 7-day step in
 * UTC drifted by an hour twice a year, which is fine for a private planner and
 * wrong once the same series is also on two phones.
 *
 * `skip` drops single dates and round-trips as EXDATE. `exceptions` drops dates
 * the external calendar has overridden with an instance of its own (a moved or
 * cancelled occurrence, which arrives as a separate event); it never leaves the
 * app, so it cannot be echoed back as an EXDATE onto an override.
 */
const dateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const recurrenceFreq = z.enum(['daily', 'weekly', 'monthly', 'yearly', 'custom']);
export const recurrenceRule = z.object({
    freq: recurrenceFreq,
    /** Every N periods. 1 = every period. */
    interval: z.number().int().positive().max(366).default(1),
    /** Inclusive end instant; null/absent = forever. */
    until: isoTimestamp.nullish(),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    nth: z.number().int().min(-1).max(5).refine((n) => n !== 0).optional(),
    /**
     * Local `YYYY-MM-DD` dates to drop — a single skipped occurrence. Kept small;
     * a rule that has skipped half a year is really two rules.
     */
    skip: z.array(dateKey).max(200).default([]),
    exceptions: z.array(dateKey).max(400).default([]),
    /** `custom` only: the external calendar's own RRULE/EXDATE lines, verbatim. */
    rrule: z.array(z.string().max(1000)).max(10).optional(),
});
const MS_PER_DAY = 86_400_000;
const keyFormatters = new Map();
/** The `YYYY-MM-DD` an instant falls on in the household's timezone. */
export function localDateKey(ms, tz = HOUSEHOLD_TIMEZONE) {
    let fmt = keyFormatters.get(tz);
    if (!fmt) {
        // en-CA renders as YYYY-MM-DD, which is the key shape used everywhere here.
        fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        keyFormatters.set(tz, fmt);
    }
    return fmt.format(new Date(ms));
}
const partFormatters = new Map();
/** Local wall-clock fields of an instant (seconds precision). */
function zonedFields(ms, tz) {
    let fmt = partFormatters.get(tz);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        partFormatters.set(tz, fmt);
    }
    const p = {};
    for (const part of fmt.formatToParts(new Date(ms)))
        if (part.type !== 'literal')
            p[part.type] = part.value;
    return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second };
}
/** Milliseconds since local midnight on the wall clock (10:30 → 37 800 000), DST-proof. */
export function localTimeOfDayMs(ms, tz = HOUSEHOLD_TIMEZONE) {
    const f = zonedFields(ms, tz);
    return ((f.h * 60 + f.mi) * 60 + f.s) * 1000 + (((ms % 1000) + 1000) % 1000);
}
/** Local wall clock minus UTC, in ms, at instant `ms`. */
function tzOffsetMs(ms, tz) {
    const f = zonedFields(ms, tz);
    return Date.UTC(f.y, f.m - 1, f.d, f.h, f.mi, f.s) - Math.floor(ms / 1000) * 1000;
}
/**
 * The instant a local wall-clock time occurs in `tz`. `localMs` is the wall
 * clock written as if it were UTC (`Date.UTC(y, m, d, h, mi, …)`). A time in
 * the spring-forward gap lands an hour later, like a phone does.
 */
export function zonedToInstant(localMs, tz = HOUSEHOLD_TIMEZONE) {
    const first = localMs - tzOffsetMs(localMs, tz);
    const second = localMs - tzOffsetMs(first, tz);
    return second;
}
const dayNumOfKey = (key) => Date.parse(`${key}T00:00:00Z`) / MS_PER_DAY;
const daysInMonth = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
/** The day number of the `nth` (1–5, -1 = last) `weekday` in month `m` of `y`, or null. */
function nthWeekdayOfMonth(y, m, weekday, nth) {
    const dim = daysInMonth(y, m);
    if (nth > 0) {
        const firstDow = new Date(Date.UTC(y, m, 1)).getUTCDay();
        const date = 1 + ((weekday - firstDow + 7) % 7) + (nth - 1) * 7;
        return date <= dim ? Date.UTC(y, m, date) / MS_PER_DAY : null;
    }
    const lastDow = new Date(Date.UTC(y, m, dim)).getUTCDay();
    return Date.UTC(y, m, dim - ((lastDow - weekday + 7) % 7)) / MS_PER_DAY;
}
/** Period index (0 = the base's own period) containing local day `day`. */
function periodOf(baseDay, rule, day) {
    const n = rule.interval || 1;
    const b = new Date(baseDay * MS_PER_DAY);
    const t = new Date(day * MS_PER_DAY);
    switch (rule.freq) {
        case 'daily':
            return Math.floor((day - baseDay) / n);
        case 'weekly': {
            const baseMonday = baseDay - ((b.getUTCDay() + 6) % 7);
            return Math.floor((day - baseMonday) / 7 / n);
        }
        case 'monthly':
            return Math.floor(((t.getUTCFullYear() - b.getUTCFullYear()) * 12 + (t.getUTCMonth() - b.getUTCMonth())) / n);
        case 'yearly':
            return Math.floor((t.getUTCFullYear() - b.getUTCFullYear()) / n);
        default:
            return 0;
    }
}
/** The candidate local days of period `k`, ascending (before skip/until/base pruning). */
function periodDays(baseDay, rule, k) {
    const p = k * (rule.interval || 1);
    const b = new Date(baseDay * MS_PER_DAY);
    const by = b.getUTCFullYear();
    const bm = b.getUTCMonth();
    const bd = b.getUTCDate();
    const bw = b.getUTCDay();
    switch (rule.freq) {
        case 'daily':
            return [baseDay + p];
        case 'weekly': {
            const monday = baseDay - ((bw + 6) % 7) + 7 * p;
            const days = rule.weekdays && rule.weekdays.length > 0 ? rule.weekdays : [bw];
            return [...new Set(days.map((w) => (w + 6) % 7))].sort((x, y) => x - y).map((off) => monday + off);
        }
        case 'monthly': {
            const months = bm + p;
            const y = by + Math.floor(months / 12);
            const m = ((months % 12) + 12) % 12;
            if (rule.nth) {
                const day = nthWeekdayOfMonth(y, m, rule.weekdays?.[0] ?? bw, rule.nth);
                return day === null ? [] : [day];
            }
            return bd <= daysInMonth(y, m) ? [Date.UTC(y, m, bd) / MS_PER_DAY] : [];
        }
        case 'yearly': {
            const y = by + p;
            return bd <= daysInMonth(y, bm) ? [Date.UTC(y, bm, bd) / MS_PER_DAY] : [];
        }
        default:
            return k === 0 ? [baseDay] : [];
    }
}
/**
 * The start instants of `rule` from period `fromPeriod` on, ascending, before
 * `skip`/`exceptions`/`until` — the RRULE set proper. Bounded so a malformed
 * rule can never spin.
 */
function* candidateStarts(startMs, rule, fromPeriod, tz) {
    const baseKey = localDateKey(startMs, tz);
    const baseDay = dayNumOfKey(baseKey);
    const timeOfDay = localTimeOfDayMs(startMs, tz);
    const periods = rule.freq === 'custom' ? 1 : 20_000;
    for (let k = Math.max(0, fromPeriod); k < periods; k++) {
        for (const day of periodDays(baseDay, rule, k)) {
            if (day < baseDay)
                continue;
            yield day === baseDay ? startMs : zonedToInstant(day * MS_PER_DAY + timeOfDay, tz);
        }
    }
}
/**
 * RRULE `COUNT=n` → the inclusive `until` instant (the nth occurrence's start),
 * so the rule needs one end representation. Null if the rule has fewer.
 */
export function untilFromCount(base, rule, count, tz = HOUSEHOLD_TIMEZONE) {
    const start = Date.parse(base.starts_at);
    if (!Number.isFinite(start) || count < 1)
        return null;
    let i = 0;
    for (const s of candidateStarts(start, rule, 0, tz)) {
        if (++i === count)
            return new Date(s).toISOString();
    }
    return null;
}
/**
 * Every occurrence of `base` that overlaps `[rangeStart, rangeEnd)`.
 *
 * A one-off (no rule) yields at most one occurrence. A rule is walked period by
 * period from just before the range (never from the base for years), keeping
 * the base's local time of day; `skip`, `exceptions` and `until` prune it.
 * All-day rows work unchanged under both conventions that write them (local
 * midnight from the app and Huib, UTC midnight from calendar sync): either is a
 * fixed local time early on the right date, and stays on that date. A
 * timed occurrence keeps the base's duration in real time, and is bucketed by
 * the local date it starts on.
 */
export function expandOccurrences(base, rangeStart, rangeEnd, tz = HOUSEHOLD_TIMEZONE) {
    const start = Date.parse(base.starts_at);
    if (!Number.isFinite(start))
        return [];
    const end = base.ends_at ? Date.parse(base.ends_at) : null;
    const duration = end !== null && Number.isFinite(end) ? Math.max(0, end - start) : null;
    const rangeStartMs = Date.parse(rangeStart);
    const rangeEndMs = Date.parse(rangeEnd);
    const emit = (s) => ({
        starts_at: new Date(s).toISOString(),
        ends_at: duration !== null ? new Date(s + duration).toISOString() : null,
        occurrence_date: localDateKey(s, tz),
    });
    const overlapsRange = (s) => {
        const e = duration !== null ? s + duration : s;
        // A zero-duration point counts as present on the instant it occupies.
        return e > rangeStartMs && s < rangeEndMs || (duration === null && s >= rangeStartMs && s < rangeEndMs);
    };
    const rule = base.recurrence ?? null;
    if (!rule) {
        return overlapsRange(start) ? [emit(start)] : [];
    }
    const untilMs = rule.until ? Date.parse(rule.until) : Number.POSITIVE_INFINITY;
    const dropped = new Set([...(rule.skip ?? []), ...(rule.exceptions ?? [])]);
    // Start one period before the one holding (rangeStart − duration), so an
    // occurrence whose tail overlaps the range's start is caught.
    const baseDay = dayNumOfKey(localDateKey(start, tz));
    const earliest = rangeStartMs - (duration ?? 0);
    const fromPeriod = earliest > start ? periodOf(baseDay, rule, dayNumOfKey(localDateKey(earliest, tz))) - 1 : 0;
    const out = [];
    for (const s of candidateStarts(start, rule, fromPeriod, tz)) {
        if (s > untilMs || s >= rangeEndMs)
            break;
        if (!overlapsRange(s))
            continue;
        if (dropped.has(localDateKey(s, tz)))
            continue;
        out.push(emit(s));
        if (out.length > 400)
            break; // a guard; a week never needs this many
    }
    return out;
}
//# sourceMappingURL=recurrence.js.map